import { useContext, useEffect, useState } from 'react'

import { faSignalStream, faSpinner } from '@fortawesome/pro-regular-svg-icons'
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, InputAdornment, TextField, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Alert } from '@material-ui/lab'
import { ConfirmationDialog } from '@scoir/modals'

import IntegrationConfigurationContext from '../IntegrationConfigurationProvider/Context'

const useStyles = makeStyles(() => ({
  formField: {
    width: '60%',
    minWidth: 300,
  },
  confirmButton: {
    height: 'fit-content',
  },
}))

const EasySetup = ({ backToOptions }: { backToOptions: () => void }) => {
  const classes = useStyles()

  const { onConfigurationChange, onSaveSlateDomain, onTestSlateConnection, slateTestSuccessful, state } = useContext(
    IntegrationConfigurationContext,
  )
  const [testing, setTesting] = useState(false)
  const [newDomain, setNewDomain] = useState(state.Slate?.Subdomain ?? '')
  const [domainEdited, setDomainEdited] = useState(false)

  useEffect(() => {
    if (slateTestSuccessful !== undefined) {
      setTesting(false)
    }
  }, [slateTestSuccessful])

  const handleSaveAndClose = () => {
    // Save domain if not already saved when tested
    if (newDomain !== state.Slate?.Subdomain && slateTestSuccessful === undefined) {
      onSaveSlateDomain({ domain: newDomain })
    }
    backToOptions()
  }

  const [open, setOpen] = useState(false)
  const handleConfiguredDeliveryMethod = () => {
    onConfigurationChange({ type: 'slate' })
    backToOptions()
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      gridGap={24}
    >
      <Button
        variant="text"
        color="primary"
        startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
        onClick={backToOptions}
      >
        Back to options
      </Button>
      <Typography>
        Add the Scoir Service Account and the ScoirMaterials source format in Slate. Then, enter your user details below
        and test the connection to verify the integration.{' '}
        <a
          href="https://help.scoir.com/article/oogwl4mkqh-new-preparing-slate-for-scoir-documents-and-record-creation"
          rel="noopener noreferrer"
          target="_blank"
        >
          Need more help? Learn more.
        </a>
      </Typography>
      <TextField
        value={newDomain}
        className={classes.formField}
        label="Username"
        onChange={(e) => {
          setNewDomain(e.target.value)
          setDomainEdited(e.target.value !== state.Slate?.Subdomain)
        }}
        variant="outlined"
        margin="normal"
        id="username-domain-editor"
        name="Username"
        autoComplete="username"
        InputProps={{
          startAdornment: <InputAdornment position="start">scoir@</InputAdornment>,
        }}
        FormHelperTextProps={{ role: 'alert' }}
      />
      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          setDomainEdited(false)
          setTesting(true)
          onTestSlateConnection({ domain: newDomain })
        }}
        disabled={!newDomain || testing}
        startIcon={<FontAwesomeIcon icon={testing ? faSpinner : faSignalStream} />}
      >
        {testing ? 'Testing...' : 'Test Connection'}
      </Button>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
      >
        {slateTestSuccessful !== undefined && !testing ? (
          <Alert severity={slateTestSuccessful ? 'success' : 'error'}>
            {slateTestSuccessful
              ? 'Test Connection was successful.'
              : 'Test Connection failed. Please try again or contact support.'}
          </Alert>
        ) : (
          // Alignment purposes
          <div />
        )}
        <Box
          display="flex"
          gridGap={16}
        >
          {slateTestSuccessful && !domainEdited && (
            <Button
              variant="text"
              color="primary"
              className={classes.confirmButton}
              disabled={!newDomain || testing}
              onClick={handleSaveAndClose}
            >
              Save & Close
            </Button>
          )}

          <Button
            variant="contained"
            color="primary"
            className={classes.confirmButton}
            disabled={!slateTestSuccessful || domainEdited}
            onClick={() => {
              if (state.SFTP?.Status === 'Live') {
                setOpen(true)
                return
              }
              handleConfiguredDeliveryMethod()
            }}
          >
            Use Configuration
          </Button>
        </Box>
      </Box>
      <ConfirmationDialog
        open={open}
        headerText="Use Configuration?"
        onClose={() => setOpen(false)}
        confirmationText="Update Configuration"
        onConfirm={handleConfiguredDeliveryMethod}
      >
        <Typography>
          Are you sure you want to switch your configuration to use Easy Slate Setup? Changes will apply to both
          Document Delivery and Handraisers.
        </Typography>
      </ConfirmationDialog>
    </Box>
  )
}

export default EasySetup
